import { CONFIG } from '../config';
import { apexTakeoverScene, RANDOM_EVENTS, taxRefundScene, type EventDef } from '../content/events';
import { needItem, needMoney, type GameContext, type Scene } from '../content/types';
import { formatMoney, round2 } from '../core/math';
import { hourOf, rentDueDay } from '../state/rules';

const DARLENE = { id: 'darlene', name: 'Darlene', voice: 330 };

export interface DirectorHooks {
  /** Only start events when no UI is open and the player isn't mid-minigame. */
  canInterrupt: () => boolean;
  /** Random events stay away while the player is busy (chasing Peanut, mid-job, driving). */
  allowRandom: () => boolean;
  onEvicted: () => void;
}

/** Decides what happens when: rent day, story beats and random events. */
export class Director {
  private lastHour = -1;

  constructor(
    private readonly ctx: GameContext,
    private readonly hooks: DirectorHooks,
  ) {}

  update(): void {
    const { store } = this.ctx;
    if (store.state.status !== 'playing' || !this.hooks.canInterrupt()) return;
    if (this.checkScheduled()) return;
    const hour = hourOf(store.minutes);
    if (hour !== this.lastHour) {
      const first = this.lastHour === -1;
      this.lastHour = hour;
      if (!first) this.rollRandomEvent(hour);
    }
  }

  /** Fixed story beats. Returns true if one fired this frame. */
  private checkScheduled(): boolean {
    const { store, ui } = this.ctx;
    const s = store.state;
    const day = s.time.day;
    const min = s.time.minutes;
    const week = store.week;

    // Rent day (Friday 5 PM), with an optional one-day grace period.
    const due = rentDueDay(week);
    const grace = Number(store.flag(`rent_grace_w${week}`) ?? 0);
    const graceActive = grace > 0 && (day < grace || (day === grace && min < 12 * 60));
    if (
      s.rent.paidThroughWeek < week &&
      !store.flag(`rent_checked_w${week}`) &&
      !graceActive &&
      (day > due || (day === due && min >= CONFIG.rentCheckMinutes))
    ) {
      ui.openScene(this.rentScene());
      return true;
    }

    if (
      store.dayOfWeek === 3 &&
      min >= 10 * 60 &&
      s.rent.paidThroughWeek < week &&
      !store.flag(`remind_w${week}`)
    ) {
      store.setFlag(`remind_w${week}`, true);
      store.toast(
        `Darlene: "Rent's due TOMORROW, sugar. $${store.rentStatus().amount}."`,
        'bad',
        ':trailer:',
      );
    }

    if (day >= 8 && min >= 9 * 60 && !store.flag('apex')) {
      store.setFlag('apex', true);
      store.post({
        author: 'Apex Capital Holdings LLC',
        avatar: 'apex',
        text: 'We are thrilled to welcome Lucky Acres to the Apex family! :briefcase::chart-up: #Community #Value',
        likes: 2,
      });
      ui.openScene(apexTakeoverScene());
      return true;
    }

    if (day >= 10 && min >= 10 * 60 && !store.flag('tax_refund')) {
      store.setFlag('tax_refund', true);
      store.earn(600);
      this.ctx.sfx('cash');
      ui.openScene(taxRefundScene());
      return true;
    }

    const investDay = Number(store.flag('dale_invest_day') ?? 0);
    if (investDay > 0 && day >= investDay && min >= 9 * 60) {
      store.setFlag('dale_invest_day', 0);
      const win = store.flag('dale_invest_win') === true;
      ui.openScene({
        title: win ? 'Dale Delivered?!' : 'About That Investment...',
        speaker: { id: 'dale', name: 'Cousin Dale', voice: 210 },
        text: win
          ? "'PARTNER! It WORKED! Campers love it! Here's your cut!' He hands you a wad of cash that smells like campfire and regret. Mostly campfire."
          : "'So. Funny story. The park ranger was there. He was not a fan.' Your thirty dollars are now evidence.",
        choices: [],
        leave: win ? "Pleasure doin' business" : 'Of course',
      });
      if (win) {
        store.earn(90);
        store.addClout(3);
        this.ctx.sfx('cash');
      }
      return true;
    }

    const mlm = Number(store.flag('mlm_joined') ?? 0);
    if (mlm > 0 && !store.flag('mlm_resolved') && day >= mlm + 2 && min >= 10 * 60) {
      store.setFlag('mlm_resolved', true);
      const paid = this.ctx.rng.chance(0.2);
      if (paid) store.earn(120);
      ui.openScene({
        title: paid ? 'Commission Check!' : 'Your Downline Update',
        speaker: { id: 'tammy', name: 'Tammy Lynn', voice: 420 },
        text: paid
          ? "'HUN!! Your first commission check!! $120!! I TOLD you!' You're as shocked as anyone. Statistically, you shouldn't be here."
          : "'So your downline is... just your mama. And she wants a refund. But hun, you just need to invest in MORE inventory!' You do not.",
        choices: [],
        leave: paid ? 'Boss babe energy' : 'Never again',
      });
      return true;
    }
    return false;
  }

  private rentScene(): Scene {
    const { store } = this.ctx;
    const r = store.rentStatus();
    const strikes = store.state.rent.strikes;
    const offered = !!store.flag(`rent_beer_w${r.week}`);
    return {
      title: ':trailer: RENT DAY',
      speaker: DARLENE,
      text: `*knock knock knock* 'Evenin', sugar. It's Friday. Lot rent's $${r.amount}.${
        store.flag('apex') ? " Don't look at me like that, the new owners set it." : ''
      }${strikes > 0 ? ` You're sittin' on ${strikes} strike${strikes > 1 ? 's' : ''}, and you know three's the limit.` : ''}'`,
      leave: false,
      choices: [
        {
          label: `Pay $${r.amount}`,
          hint: `-$${r.amount}`,
          requires: needMoney(r.amount),
          run: ({ store, sfx }) => {
            store.payRent();
            store.setFlag(`rent_checked_w${r.week}`, true);
            sfx('cash');
            return {
              text: `Darlene licks her thumb and counts every bill. 'Pleasure doin' business.'${this.settleDebt()}`,
            };
          },
        },
        ...(offered
          ? []
          : [
              {
                label: 'Offer her a cold one',
                hint: '-1 :beer:',
                requires: needItem('beer', 1, 'beer'),
                run: ({ store, rng }: GameContext) => {
                  store.take('beer');
                  store.setFlag(`rent_beer_w${r.week}`, true);
                  if (rng.chance(0.4)) {
                    store.setFlag(`rent_grace_w${r.week}`, store.day + 1);
                    return "Darlene cracks it, takes a long sip, and sighs. 'Fine. Tomorrow by noon. Don't make me regret bein' nice.'";
                  }
                  return {
                    ...this.rentScene(),
                    text: "Darlene takes the beer, drinks it in one go, and holds out her other hand. 'That was a tip. Rent's still due, hon.'",
                  };
                },
              },
            ]),
        {
          label: "I'll have it Monday, I swear",
          hint: '+1 Strike',
          run: ({ store }) => {
            store.setFlag(`rent_checked_w${r.week}`, true);
            const evicted = store.addStrike();
            const n = store.state.rent.strikes;
            if (evicted) {
              this.hooks.onEvicted();
              return "Darlene's face falls. 'That's three, hon. I'm sorry. It ain't me, it's them.' She tapes a bright orange notice to your door.";
            }
            const debtNote = this.settleDebt(false);
            return `'That's strike ${n}, sugar. ${n === 2 ? "One more and Apex sends the sheriff. I can't stop 'em." : "Don't make it a habit."}'${debtNote}`;
          },
        },
      ],
    };
  }

  /** Weekly payday-loan garnish / interest, settled when rent is collected. */
  private settleDebt(canPay = true): string {
    const { store } = this.ctx;
    const debt = store.state.debt;
    if (debt <= 0) return '';
    if (canPay && store.money > 0) {
      const pay = Math.min(debt, store.money);
      store.spend(pay);
      store.addDebt(-pay);
      if (store.state.debt <= 0)
        return `\n\nLucky's Payday also garnished ${formatMoney(pay)}. Your debt is CLEARED.`;
    }
    const interest = round2(store.state.debt * CONFIG.debtInterest);
    store.addDebt(interest);
    return `\n\nYou still owe Lucky's Payday ${formatMoney(store.state.debt)} (+${formatMoney(interest)} interest this week).`;
  }

  private rollRandomEvent(hour: number): void {
    const { store, rng } = this.ctx;
    if (hour < 8 && hour > 2) return;
    if (store.day === 1 && hour < 12) return; // let new players get their bearings
    if (!this.hooks.allowRandom()) return;
    const last = Number(store.flag('last_event_at') ?? -9999);
    if (store.now - last < 180) return;
    const today = Number(store.flag(`events_d${store.day}`) ?? 0);
    if (today >= 2 || !rng.chance(0.15)) return;
    const eligible = RANDOM_EVENTS.filter((e) => this.eligible(e, hour));
    const pick = rng.weighted(eligible, (e) => e.weight);
    if (!pick) return;
    store.setFlag(`events_d${store.day}`, today + 1);
    store.setFlag('last_event_at', store.now);
    store.setFlag(`ev_${pick.id}`, store.day);
    if (pick.once) store.setFlag(`evonce_${pick.id}`, true);
    this.ctx.ui.openScene(pick.start(this.ctx));
  }

  private eligible(e: EventDef, hour: number): boolean {
    const { store } = this.ctx;
    if (e.minDay && store.day < e.minDay) return false;
    if (e.once && store.flag(`evonce_${e.id}`)) return false;
    const last = store.flag(`ev_${e.id}`);
    if (typeof last === 'number' && e.cooldownDays && store.day - last < e.cooldownDays) return false;
    if (e.hours) {
      const [start, end] = e.hours;
      const h = hour < 6 ? hour + 24 : hour;
      if (h < start || h >= end) return false;
    }
    return e.when ? e.when(this.ctx) : true;
  }

  /** Force a specific event (debug / tests). */
  trigger(id: string): boolean {
    const e = RANDOM_EVENTS.find((x) => x.id === id);
    if (!e) return false;
    this.ctx.ui.openScene(e.start(this.ctx));
    return true;
  }
}
